import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * App-level error boundary.
 *
 * Without one, any exception thrown during render unmounts the whole tree and
 * leaves a blank white page — the user has no idea what happened and no way
 * back except a manual reload. Large result sets make this more likely (a
 * malformed score, an unexpected null in a deeply-mapped row), and that is
 * exactly when losing the whole page is most costly.
 *
 * This catches render/lifecycle errors below it and shows a recoverable panel:
 * "Try again" re-mounts the subtree (cheap — no reload, react-query keeps its
 * cache), and reloading is offered as the fallback. Errors are logged to the
 * console so they still surface in dev tools and error reporting.
 *
 * Note what a boundary does NOT catch: errors in event handlers, async
 * callbacks, or promise rejections. Those are handled where they occur (e.g.
 * the CSV export's try/catch) — a boundary is the last line of defence, not a
 * substitute for local handling.
 */
interface Props {
  children: ReactNode;
  /** Optional label so the message can name the area that failed. */
  area?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error', error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const area = this.props.area ? ` in ${this.props.area}` : '';
    return (
      <div className="m-6 max-w-2xl border border-ink-200 rounded-sm bg-white p-6">
        <h2 className="font-display text-lg text-ink-900 mb-2">
          Something went wrong{area}
        </h2>
        <p className="text-sm text-ink-600 mb-4">
          The page hit an unexpected error and stopped rendering. Your data is
          unaffected — nothing was saved or changed by this.
        </p>
        <pre className="text-[11px] font-mono bg-ink-50 border border-ink-100 rounded p-3 mb-4 overflow-x-auto whitespace-pre-wrap">
          {error.message || String(error)}
        </pre>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={this.reset}
            className="text-sm bg-accent text-white px-4 py-2 rounded hover:bg-accent-hover"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-sm text-ink-500 hover:text-ink underline"
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
